import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { css } from '@emotion/css';
import { signal } from '@preact/signals-core';
import type { TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT,
  DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT,
} from '../../../core/data-source/window.js';
import {
  createUniComponentFromWebComponent,
  dataViewI18nT,
  renderUniLit,
} from '../../../core/index.js';
import { sortable } from '../../../core/utils/wc-dnd/sort/sort-context.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../../core/view/data-view-base.js';
import { popAddGroupInput } from '../add-group-menu.js';
import { resolveKanbanVisibleGroups } from '../card-render-window.js';
import { type KanbanSingleView } from '../kanban-view-manager.js';
import type { KanbanViewSelectionWithType } from '../selection';

const mobileKanbanViewWrapper = css({
  userSelect: 'none',
  display: 'flex',
  flexDirection: 'column',
});

const mobileKanbanRows = css({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingBottom: '4px',
});

const mobileKanbanRow = css({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: '1px solid var(--streamlain-border-color)',
  paddingBottom: '8px',
});

const mobileKanbanRowHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 4px',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--streamlain-text-primary-color)',
});

const mobileKanbanRowBody = css({
  display: 'flex',
  gap: '16px',
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingTop: '6px',
});

const mobileAddGroup = css({
  height: '32px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  padding: '4px',
  borderRadius: '4px',
  fontSize: '16px',
  color: `var(${unsafeCSSVarV2('icon/primary')})`,
});

const mobileOverflow = css({
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
  color: 'var(--streamlain-text-secondary-color)',
  padding: '12px',
  textAlign: 'center',
  cursor: 'pointer',
  userSelect: 'none',

  '&:hover': {
    backgroundColor: 'var(--streamlain-hover-color)',
    color: 'var(--streamlain-text-primary-color)',
  },
});

export class MobileKanbanViewUILogic extends DataViewUILogicBase<
  KanbanSingleView,
  KanbanViewSelectionWithType
> {
  ui$ = signal<MobileKanbanViewUI | undefined>(undefined);

  /**
   * Render-window sizes — mirrors the PC logic: policy constants are
   * the initial sample, tapping an overflow tile widens the window by
   * one more policy step. Transient view state.
   */
  readonly columnRenderLimit$ = signal(DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT);

  readonly rowGroupRenderLimit$ = signal(
    DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT
  );

  showMoreColumns = () => {
    this.columnRenderLimit$.value += DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT;
  };

  showMoreRowGroups = () => {
    this.rowGroupRenderLimit$.value +=
      DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT;
  };

  private get readonly() {
    return this.view.readonly$.value;
  }

  clearSelection = () => {};

  addRow = (position: InsertToPosition) => {
    if (this.readonly) return;
    const id = this.view.rowAdd(position);
    this.ui$.value?.requestUpdate();
    return id;
  };

  focusFirstCell = () => {};

  showIndicator = (_evt: MouseEvent) => {
    return false;
  };

  hideIndicator = () => {};

  moveTo = () => {};

  get groupManager() {
    return this.view.groupTrait;
  }

  get group2Manager() {
    return this.view.group2Trait;
  }

  findCardRow(cardId: string): string | undefined {
    const map = this.group2Manager?.groupDataMap$.value;
    if (!map) return undefined;
    for (const [key, group] of Object.entries(map)) {
      if (group?.rows.some(r => r.rowId === cardId)) return key;
    }
    return undefined;
  }

  renderAddGroup = () => {
    if (this.readonly) {
      return;
    }
    const addGroup = this.groupManager.addGroup;
    if (!addGroup) {
      return;
    }
    const add = (e: MouseEvent) => {
      const ele = e.currentTarget as HTMLElement;
      popAddGroupInput(
        popupTargetFromElement(ele),
        this.groupManager,
        this.view.manager.dataSource
      );
    };
    return html` <div class="${mobileAddGroup}" @click="${add}">
      ${AddCursorIcon()}
    </div>`;
  };

  renderer = createUniComponentFromWebComponent(MobileKanbanViewUI);
}

export class MobileKanbanViewUI extends DataViewUIBase<MobileKanbanViewUILogic> {
  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.classList.add(mobileKanbanViewWrapper);
  }

  override render(): TemplateResult {
    const cols = this.logic.groupManager.groupsDataList$.value;
    const rows = this.logic.group2Manager?.groupsDataList$.value;
    if (!cols) {
      return html``;
    }
    const colEntries = cols.filter(
      (g): g is NonNullable<(typeof cols)[number]> => g != null
    );
    const rowEntries = (rows ?? []).filter(
      (g): g is NonNullable<NonNullable<typeof rows>[number]> => g != null
    );
    const { visibleGroups: visibleCols, hiddenCount: hiddenColCount } =
      resolveKanbanVisibleGroups(
        colEntries,
        this.logic.columnRenderLimit$.value
      );
    const { visibleGroups: visibleRows, hiddenCount: hiddenRowCount } =
      resolveKanbanVisibleGroups(
        rowEntries,
        this.logic.rowGroupRenderLimit$.value
      );
    const vPadding = this.logic.root.config.virtualPadding$.value;
    const wrapperStyle = styleMap({
      marginLeft: `-${vPadding}px`,
      marginRight: `-${vPadding}px`,
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
    });
    const renderColumnGroups = (rowKey: string) => html`
      <div class="${mobileKanbanRowBody}">
        ${repeat(
          visibleCols,
          col => col.key,
          col =>
            html`<mobile-kanban-group
              ${sortable(`${rowKey}::${col.key}`)}
              data-key="${col.key}"
              data-row-key="${rowKey}"
              .rowKey="${rowKey}"
              .kanbanViewLogic="${this.logic}"
              .group="${col}"
            ></mobile-kanban-group>`
        )}
        ${hiddenColCount > 0
          ? html`<div
              class="${mobileOverflow}"
              role="button"
              title="${dataViewI18nT('kanban.showMoreColumnsTitle')}"
              data-hidden-kanban-group-count="${hiddenColCount}"
              style="width:260px;flex-shrink:0;"
              @click="${this.logic.showMoreColumns}"
            >
              ${dataViewI18nT('kanban.showMoreColumns', {
                count: hiddenColCount,
              })}
            </div>`
          : html``}
      </div>
    `;
    return html`
      ${renderUniLit(this.logic.headerWidget, {
        dataViewLogic: this.logic,
      })}
      <div class="${mobileKanbanRows}" style="${wrapperStyle}">
        ${visibleRows.length === 0
          ? renderColumnGroups('')
          : repeat(
              visibleRows,
              row => row.key,
              row => html`
                <div class="${mobileKanbanRow}" data-row-key="${row.key}">
                  <div
                    class="${mobileKanbanRowHeader}"
                    @click="${() => {
                      this.logic.view.toggleRow2Collapsed(row.key);
                      this.requestUpdate();
                    }}"
                  >
                    <span>${row.name$.value || 'Unassigned'}</span>
                    <span style="opacity:0.6;">(${row.rows.length})</span>
                  </div>
                  ${this.logic.view.isRow2Collapsed(row.key)
                    ? html``
                    : renderColumnGroups(row.key)}
                </div>
              `
            )}
        ${hiddenRowCount > 0
          ? html`<div
              class="${mobileOverflow}"
              role="button"
              title="${dataViewI18nT('kanban.showMoreRowGroupsTitle')}"
              data-hidden-kanban-row-group-count="${hiddenRowCount}"
              @click="${this.logic.showMoreRowGroups}"
            >
              ${dataViewI18nT('kanban.showMoreRowGroups', {
                count: hiddenRowCount,
              })}
            </div>`
          : html``}
        ${this.logic.renderAddGroup()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mobile-data-view-kanban-ui': MobileKanbanViewUI;
  }
}
